(() => {
  'use strict';
  if (window.C1OptimizedLayer) return;
  window.C1OptimizedLayer = true;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let videoObserver = null;
  let videoPreloadObserver = null;
  const syncLazyVideo = (video) => {
    if (!(video instanceof HTMLVideoElement)) return;
    const rect = video.getBoundingClientRect();
    const panel = video.closest('[role="tabpanel"]');
    const active = !panel || (!panel.hidden && panel.getAttribute('aria-hidden') !== 'true');
    const visible = video.getClientRects().length > 0 && rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
    if (active && visible && !document.hidden && !reducedMotion()) {
      video.preload = 'metadata';
      const promise = video.play();
      if (promise && typeof promise.catch === 'function') promise.catch(() => {});
    } else {
      try { video.pause(); } catch (error) {}
    }
  };
  window.C1LazyVideoController = { sync: syncLazyVideo };

  const ensureVideoObserver = () => {
    if (videoObserver || !('IntersectionObserver' in window)) return videoObserver;
    videoObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => syncLazyVideo(entry.target));
    }, { threshold: [0, 0.01] });
    videoPreloadObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting && !document.hidden && !reducedMotion() && video instanceof HTMLVideoElement) video.preload = 'metadata';
      });
    }, { rootMargin: '360px 0px', threshold: 0.01 });
    return videoObserver;
  };

  const initLazyVideos = (root = document) => {
    const videos = [];
    if (root instanceof HTMLVideoElement && root.matches('[data-c1-lazy-video]')) videos.push(root);
    root.querySelectorAll?.('video[data-c1-lazy-video]').forEach((video) => videos.push(video));
    videos.forEach((video) => {
      if (video.dataset.c1LazyReady === 'true') return;
      video.dataset.c1LazyReady = 'true';
      if (!('IntersectionObserver' in window)) {
        syncLazyVideo(video);
        return;
      }
      ensureVideoObserver()?.observe(video);
      videoPreloadObserver?.observe(video);
    });
  };

  const syncVisibleVideos = () => document.querySelectorAll('video[data-c1-lazy-video]').forEach(syncLazyVideo);
  document.addEventListener('visibilitychange', syncVisibleVideos);
  if (!('IntersectionObserver' in window)) {
    window.addEventListener('scroll', syncVisibleVideos, { passive: true });
    window.addEventListener('resize', syncVisibleVideos, { passive: true });
  }

  const initCarousel = (carousel) => {
    if (!carousel || carousel.dataset.c1xReady === 'true') return;
    const viewport = carousel.querySelector('.c1x-carousel__viewport');
    const track = carousel.querySelector('[data-c1x-track]');
    const slides = [...carousel.querySelectorAll('[data-c1x-slide]')];
    const prevButton = carousel.querySelector('[data-c1x-prev]');
    const nextButton = carousel.querySelector('[data-c1x-next]');
    const dotsWrapper = carousel.querySelector('[data-c1x-dots]');
    if (!viewport || !track || !slides.length) return;

    carousel.dataset.c1xReady = 'true';
    const desktopQuery = window.matchMedia('(min-width: 990px)');
    const autoplayEnabled = carousel.dataset.autoplay === 'true';
    const loopEnabled = carousel.dataset.loop === 'true';
    const interval = Math.max(3000, Number(carousel.dataset.interval || 5000));
    const noMotion = reducedMotion();
    let currentIndex = 0;
    let timer = null;
    let progressRAF = null;
    let progressStart = 0;
    let progressOffset = 0;
    let paused = false;
    let inView = !('IntersectionObserver' in window);
    let mobileScrollTimer = null;

    const buildDots = () => {
      if (!dotsWrapper) return;
      dotsWrapper.innerHTML = '';
      slides.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'c1x-carousel__dot';
        const label = carousel.dataset.goToSlideLabel || '[number]';
        dot.setAttribute('aria-label', label.replace('[number]', String(index + 1)));
        dot.addEventListener('click', () => goTo(index, true));
        dotsWrapper.appendChild(dot);
      });
    };

    const updateClasses = () => {
      slides.forEach((slide, index) => {
        const active = index === currentIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-current', active ? 'true' : 'false');
      });
      [...(dotsWrapper?.children || [])].forEach((dot, index) => {
        const active = index === currentIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
        if (!active) dot.style.setProperty('--c1x-progress', '0%');
      });
    };

    const updateDesktopPosition = () => {
      if (!desktopQuery.matches) return;
      const slide = slides[currentIndex];
      if (!slide) return;
      const translateX = viewport.clientWidth / 2 - (slide.offsetLeft + slide.offsetWidth / 2);
      track.style.transform = `translate3d(${translateX}px,0,0)`;
    };

    const nearestMobileIndex = () => {
      const center = viewport.scrollLeft + viewport.clientWidth / 2;
      let nearest = 0;
      let shortest = Infinity;
      slides.forEach((slide, index) => {
        const distance = Math.abs(center - (slide.offsetLeft + slide.offsetWidth / 2));
        if (distance < shortest) { shortest = distance; nearest = index; }
      });
      return nearest;
    };

    const render = () => { updateClasses(); updateDesktopPosition(); };
    const resetProgress = () => {
      progressStart = performance.now();
      progressOffset = 0;
      dotsWrapper?.children[currentIndex]?.style.setProperty('--c1x-progress', '0%');
    };

    const stopProgress = () => {
      if (progressRAF) cancelAnimationFrame(progressRAF);
      progressRAF = null;
    };

    const animateProgress = (now) => {
      if (!inView || !autoplayEnabled || noMotion) { progressRAF = null; return; }
      if (!paused && dotsWrapper) {
        const dot = dotsWrapper.children[currentIndex];
        if (dot) {
          const elapsed = now - progressStart + progressOffset;
          dot.style.setProperty('--c1x-progress', `${Math.min(100, elapsed / interval * 100)}%`);
        }
      }
      progressRAF = requestAnimationFrame(animateProgress);
    };

    const startProgress = () => {
      if (progressRAF || noMotion || !autoplayEnabled || !inView) return;
      progressRAF = requestAnimationFrame(animateProgress);
    };

    const stopAutoplay = () => { if (timer) clearInterval(timer); timer = null; };
    const startAutoplay = () => {
      stopAutoplay();
      if (!autoplayEnabled || noMotion || slides.length < 2 || !inView) return;
      resetProgress();
      startProgress();
      timer = setInterval(() => { if (!paused) goTo(currentIndex + 1); }, interval);
    };

    function goTo(index, userTriggered = false) {
      if (index < 0) index = loopEnabled ? slides.length - 1 : 0;
      if (index >= slides.length) index = loopEnabled ? 0 : slides.length - 1;
      currentIndex = index;
      render();
      if (!desktopQuery.matches) {
        const slide = slides[currentIndex];
        if (slide) viewport.scrollTo({ left: slide.offsetLeft - (viewport.clientWidth - slide.offsetWidth) / 2, behavior: noMotion ? 'auto' : 'smooth' });
      }
      resetProgress();
      if (userTriggered) startAutoplay();
    }

    const pause = () => {
      if (paused) return;
      paused = true;
      progressOffset += performance.now() - progressStart;
    };
    const resume = () => {
      if (!paused) return;
      paused = false;
      progressStart = performance.now();
    };

    prevButton?.addEventListener('click', () => goTo(currentIndex - 1, true));
    nextButton?.addEventListener('click', () => goTo(currentIndex + 1, true));
    carousel.addEventListener('mouseenter', pause);
    carousel.addEventListener('mouseleave', resume);
    carousel.addEventListener('focusin', pause);
    carousel.addEventListener('focusout', resume);
    viewport.addEventListener('scroll', () => {
      if (desktopQuery.matches) return;
      clearTimeout(mobileScrollTimer);
      mobileScrollTimer = setTimeout(() => { currentIndex = nearestMobileIndex(); updateClasses(); resetProgress(); }, 90);
    }, { passive: true });

    const handleLayout = () => {
      if (desktopQuery.matches) viewport.scrollLeft = 0;
      else track.style.transform = 'none';
      requestAnimationFrame(render);
    };
    window.addEventListener('resize', handleLayout, { passive: true });
    desktopQuery.addEventListener?.('change', handleLayout);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { stopAutoplay(); stopProgress(); }
      else if (inView) startAutoplay();
    });

    buildDots();
    render();

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target !== carousel) return;
          inView = entry.isIntersecting;
          if (inView && !document.hidden) startAutoplay();
          else { stopAutoplay(); stopProgress(); }
        });
      }, { rootMargin: '200px 0px', threshold: 0.01 });
      observer.observe(carousel);
    } else {
      startAutoplay();
    }
  };

  const initCarousels = (root = document) => {
    if (root instanceof Element && root.matches('[data-c1x-carousel]')) initCarousel(root);
    root.querySelectorAll?.('[data-c1x-carousel]').forEach(initCarousel);
  };

  const init = (root = document) => { initLazyVideos(root); initCarousels(root); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target || document));
})();
